// @flow

import * as React from 'react';
import {useState, useEffect} from 'react';

import {isBrowser} from 'docs/util';

// This is the hard-coded line-height of a single line of code in the code
// snippet block. We want to fall back to it, in the case of SSR or when we
// aren't able to infer it.
//
// TODO (nkohari): It'd be nice for this not to be hardcoded. The old code used
// getComputedStyle() to get around this, but that's a much worse hack, especially
// in React!
const DEFAULT_CODE_LINE_HEIGHT = 19;
const DEFAULT_PADDING_TOP = 16;

const getDevicePixelRatio = (): number | null => {
  if (!isBrowser) {
    return null;
  }

  if (window.devicePixelRatio) {
    return window.devicePixelRatio;
  } else if (document && document.documentElement) {
    return window.screen.availWidth / document.documentElement.clientWidth;
  } else {
    return null;
  }
};

// Memoize the line heights at each browser zoom level
const lineHeightsForPixelRatio = {};

const getSingleLineHeight = (
  codeElement: HTMLElement,
  ratio: number | null,
): number => {
  if (!ratio) {
    return DEFAULT_CODE_LINE_HEIGHT;
  }

  if (lineHeightsForPixelRatio[ratio]) {
    return lineHeightsForPixelRatio[ratio];
  }

  if (!codeElement) {
    return DEFAULT_CODE_LINE_HEIGHT;
  }

  const parentNode = codeElement.parentNode;

  if (!parentNode) {
    return DEFAULT_CODE_LINE_HEIGHT;
  }

  // Clone the <code> block and insert it as a sibling, so it has the
  // exact same styles as the real <code> block.
  const deepClone = false;
  const clonedCode = codeElement.cloneNode(deepClone);

  // Set absolute/hidden so we can still measure the computed height.
  clonedCode.style.display = 'block';
  clonedCode.style.position = 'absolute';
  clonedCode.style.visibility = 'hidden';

  parentNode.appendChild(clonedCode);

  // Measure height with 1 line of text
  clonedCode.innerText = 'line 1';

  const originalRect = clonedCode.getBoundingClientRect();
  const originalHeight = originalRect.bottom - originalRect.top;

  // Measure height with 2 lines of text
  clonedCode.innerText = 'line 1\nline 2';
  const currentRect = clonedCode.getBoundingClientRect();
  const tallerHeight = currentRect.bottom - currentRect.top;

  parentNode.removeChild(clonedCode);

  // Save it so we only calculate once per zoom level
  lineHeightsForPixelRatio[ratio] = Math.max(0, tallerHeight - originalHeight);

  return lineHeightsForPixelRatio[ratio];
};

type HighlightStyle = {|
  top: string,
  height: string,
|};

type UseCodeLineHeightOutput = {|
  getHighlightStyle: (startLine: number, endLine: number) => HighlightStyle,
|};

export const useCodeLineHeight = (
  codeBlockRef: React.ElementRef<any>,
): UseCodeLineHeightOutput => {
  const [deviceRatio, setDeviceRatio] = useState(getDevicePixelRatio());

  // If the user resizes the browser with cmd + plus/minus, we want to recompute
  // the line height based on the new device ratio.
  useEffect(() => {
    const updateDeviceRatio = () => {
      const newRatio = getDevicePixelRatio();
      setDeviceRatio(newRatio);
    };
    if (codeBlockRef.current) {
      window.addEventListener('resize', updateDeviceRatio);

      return () => {
        // Clean up prior listener if it exists.
        window.removeEventListener('resize', updateDeviceRatio);
      };
    }
  }, [codeBlockRef]);

  const paddingTop = DEFAULT_PADDING_TOP;
  const lineHeight = getSingleLineHeight(codeBlockRef.current, deviceRatio);

  const getHighlightStyle = (
    startLine: number,
    endLine: number,
  ): HighlightStyle => {
    return {
      top: `${(startLine - 1) * lineHeight + paddingTop}px`,
      height: `${(endLine - startLine + 1) * lineHeight}px`,
    };
  };

  return {getHighlightStyle};
};
